import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Search, Wrench, CheckCircle, Clock, AlertTriangle,
  Settings, FileText, Trash2, Edit, X, Download, Filter,
  User, Gauge, Package, FileSpreadsheet, BarChart3, ArrowRight, Camera, ChevronLeft
} from 'lucide-react';
import { BinArticleSelector, parseBinQrCode } from '../components/ui/bin-article-selector';
import { useQrScanner } from '../hooks/useQrScanner';

const TIPS = [
  { value: 'reparatie', label: 'Reparatie' },
  { value: 'revizie', label: 'Revizie' },
  { value: 'mentenanta_preventiva', label: 'Mentenanta Preventiva' },
  { value: 'avarie', label: 'Avarie' },
];
const PRIORITATI = [
  { value: 'scazuta', label: 'Scazuta', color: 'bg-slate-100 text-slate-600' },
  { value: 'medie', label: 'Medie', color: 'bg-blue-100 text-blue-700' },
  { value: 'urgenta', label: 'Urgenta', color: 'bg-red-100 text-red-700' },
];
const STATUSES = [
  { value: 'deschis', label: 'Deschis', color: 'bg-amber-100 text-amber-700' },
  { value: 'in_lucru', label: 'In Lucru', color: 'bg-blue-100 text-blue-700' },
  { value: 'finalizat', label: 'Finalizat', color: 'bg-emerald-100 text-emerald-700' },
];

const ServiceLizePage = () => {
  const { user, hasRole } = useAuth();
  const [equipment, setEquipment] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEqId, setSelectedEqId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editIntervention, setEditIntervention] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [viewMode, setViewMode] = useState('equipment'); // equipment, dashboard
  const [articleSearch, setArticleSearch] = useState('');
  const [binScanOpen, setBinScanOpen] = useState(false);
  const [binScanCode, setBinScanCode] = useState('');
  const [binScanType, setBinScanType] = useState('bin');
  const [showQrScanner, setShowQrScanner] = useState(false);
  const { start: startQr, stop: stopQr } = useQrScanner();
  const [form, setForm] = useState({
    equipment_id: '', tip: 'reparatie', prioritate: 'medie', descriere: '',
    lucrari_efectuate: '', index_ore: '', technician_id: '', technician_name: '',
    data_interventie: '', observatii: '', consumabile: []
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [eqRes, usersRes] = await Promise.all([
        api.get('/iscir-equipment'),
        api.get('/users'),
      ]);
      setEquipment(eqRes.data);
      setUsers(usersRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const fetchInterventions = useCallback(async () => {
    try {
      const params = selectedEqId ? { equipment_id: selectedEqId } : {};
      const res = await api.get('/service-lize/interventions', { params });
      setInterventions(res.data);
    } catch (e) { console.error(e); }
  }, [selectedEqId]);

  const fetchArticles = useCallback(async (search) => {
    try {
      const res = await api.get('/articles', { params: { search: search || '', limit: 20 } });
      setArticles(Array.isArray(res.data) ? res.data : res.data.items || []);
    } catch (e) { setArticles([]); }
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await api.get('/service-lize/dashboard');
      setDashboard(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchInterventions(); }, [fetchInterventions]);

  const selectedEq = equipment.find(e => e.id === selectedEqId);
  const eqInterventions = selectedEqId ? interventions.filter(i => i.equipment_id === selectedEqId) : interventions;

  const filteredEquipment = equipment.filter(e => {
    const s = searchTerm.toLowerCase();
    return !s || (e.denumire || '').toLowerCase().includes(s) || (e.code || '').toLowerCase().includes(s) ||
      (e.marca || '').toLowerCase().includes(s) || (e.serie_utilaj || '').toLowerCase().includes(s);
  });

  const openNewForm = () => {
    setEditIntervention(null);
    setForm({
      equipment_id: selectedEqId, tip: 'reparatie', prioritate: 'medie', descriere: '',
      lucrari_efectuate: '', index_ore: selectedEq?.ore_functionare || '', technician_id: user?.id || '',
      technician_name: user?.full_name || '', data_interventie: new Date().toLocaleDateString('ro-RO'), observatii: '', consumabile: []
    });
    setShowForm(true);
  };

  const openEditForm = (iv) => {
    setEditIntervention(iv);
    setForm({
      equipment_id: iv.equipment_id, tip: iv.tip, prioritate: iv.prioritate,
      descriere: iv.descriere || '', lucrari_efectuate: iv.lucrari_efectuate || '',
      index_ore: iv.index_ore || '', technician_id: iv.technician_id || '',
      technician_name: iv.technician_name || '', data_interventie: iv.data_interventie || '',
      observatii: iv.observatii || '', consumabile: iv.consumabile || []
    });
    setShowForm(true);
  };

  const saveIntervention = async () => {
    try {
      const payload = { ...form, index_ore: form.index_ore ? parseFloat(form.index_ore) : null };
      if (editIntervention) {
        await api.put(`/service-lize/interventions/${editIntervention.id}`, payload);
        toast.success('Interventie actualizata');
      } else {
        await api.post('/service-lize/interventions', payload);
        toast.success('Interventie creata');
      }
      setShowForm(false);
      fetchInterventions();
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
  };

  const deleteIntervention = async (id) => {
    if (!window.confirm('Sigur doriti stergerea?')) return;
    try {
      await api.delete(`/service-lize/interventions/${id}`);
      toast.success('Sters');
      fetchInterventions();
    } catch (e) { toast.error('Eroare'); }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await api.put(`/service-lize/interventions/${id}`, { status: newStatus });
      toast.success('Status actualizat');
      fetchInterventions();
    } catch (e) { toast.error('Eroare'); }
  };

  const addConsumabil = (article) => {
    setForm(prev => ({
      ...prev,
      consumabile: [...prev.consumabile, {
        article_id: article.id, article_name: article.name,
        article_code: article.code, unit: article.unit || 'buc', quantity: 1
      }]
    }));
    setArticleSearch('');
  };

  const removeConsumabil = (idx) => {
    setForm(prev => ({ ...prev, consumabile: prev.consumabile.filter((_, i) => i !== idx) }));
  };

  const updateConsumabilQty = (idx, qty) => {
    setForm(prev => ({
      ...prev,
      consumabile: prev.consumabile.map((c, i) => i === idx ? { ...c, quantity: parseFloat(qty) || 0 } : c)
    }));
  };

  const startConsumScan = () => {
    setShowQrScanner(true);
    setTimeout(async () => {
      try {
        await startQr('lize-qr-reader', async (text) => {
          await stopQr();
          setShowQrScanner(false);
          const binResult = parseBinQrCode(text);
          if (binResult.isBin) {
            setBinScanCode(binResult.value);
            setBinScanType(binResult.type);
            setBinScanOpen(true);
            return;
          }
          let searchCode = text;
          const parts = text.split(':');
          if (parts.length >= 2 && parts[0] === 'ART') searchCode = parts.slice(1).join(':');
          try {
            const resp = await api.get('/articles', { params: { search: searchCode } });
            if (resp.data.length > 0) { addConsumabil(resp.data[0]); toast.success(`Articol scanat: ${resp.data[0].name}`); }
            else toast.error('Articol negasit');
          } catch { toast.error('Eroare la cautare'); }
        }, { fps: 10, qrbox: { width: 220, height: 220 } });
      } catch { toast.error('Nu s-a putut porni camera'); setShowQrScanner(false); }
    }, 300);
  };

  const handleBinArticleSelect = (art) => {
    setBinScanOpen(false);
    addConsumabil({ id: art.article_id, name: art.article_name, code: art.article_code, unit: art.unit });
  };

  const exportExcel = async () => {
    try {
      const params = selectedEqId ? { equipment_id: selectedEqId } : {};
      const res = await api.get('/service-lize/export-excel', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.download = 'Service_Lize.xlsx'; link.click();
      toast.success('Excel exportat');
    } catch (e) { toast.error('Eroare export'); }
  };

  const getPriorityBadge = (p) => PRIORITATI.find(x => x.value === p) || PRIORITATI[1];
  const getStatusBadge = (s) => STATUSES.find(x => x.value === s) || STATUSES[0];

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col" data-testid="service-lize-page">
      <BinArticleSelector open={binScanOpen} binCode={binScanCode} scanType={binScanType} onSelect={handleBinArticleSelect} onClose={() => setBinScanOpen(false)} />
      {/* Top Bar */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b bg-white flex-shrink-0">
        <div>
          <h1 className="text-base sm:text-lg font-bold text-slate-900" data-testid="page-title">Service Lize</h1>
          <p className="text-[11px] text-slate-500 hidden sm:block">Interventii si service echipamente ISCIR</p>
        </div>
        <div className="flex gap-1.5 items-center">
          <div className="hidden sm:flex gap-0.5 bg-slate-100 rounded-lg p-0.5 mr-2">
            {[
              { key: 'equipment', icon: Wrench, label: 'Echipamente' },
              { key: 'dashboard', icon: BarChart3, label: 'Dashboard' },
            ].map(v => (
              <button key={v.key} onClick={() => { setViewMode(v.key); if (v.key === 'dashboard') fetchDashboard(); }}
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  viewMode === v.key ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`} data-testid={`view-${v.key}`}>
                <v.icon className="w-3.5 h-3.5" />{v.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={exportExcel} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /><span className="hidden lg:inline">Excel</span>
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Panel - Equipment List */}
        <div className={`${selectedEqId && viewMode === 'equipment' ? 'hidden lg:flex' : 'flex'} w-full lg:w-80 border-r bg-white flex-col flex-shrink-0 overflow-hidden`}>
          <div className="p-3 space-y-2 border-b">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-8 h-8 text-xs" placeholder="Cauta liza..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} data-testid="search-equipment" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredEquipment.map(eq => {
              const isSelected = eq.id === selectedEqId;
              const eqIvCount = interventions.filter(i => i.equipment_id === eq.id).length;
              const hasOpen = interventions.some(i => i.equipment_id === eq.id && i.status !== 'finalizat');
              return (
                <button key={eq.id} onClick={() => { setSelectedEqId(eq.id); setViewMode('equipment'); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left border-b border-slate-100 transition-colors hover:bg-slate-50 border-l-[3px] ${
                    isSelected ? 'bg-blue-50 border-l-blue-600' : 'border-l-transparent'
                  }`} data-testid={`eq-${eq.id}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${hasOpen ? 'bg-amber-100' : 'bg-emerald-100'}`}>
                    <Wrench className={`w-4 h-4 ${hasOpen ? 'text-amber-600' : 'text-emerald-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate">{eq.denumire}</p>
                    <p className="text-[10px] text-slate-400">{eq.code} · {eq.marca || ''}</p>
                  </div>
                  {eqIvCount > 0 && (
                    <span className="text-[9px] font-bold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full">{eqIvCount}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel */}
        <div className={`${!selectedEqId && viewMode !== 'dashboard' ? 'hidden lg:flex' : 'flex'} flex-1 flex-col overflow-hidden`}>
          {viewMode === 'dashboard' ? (
            <DashboardView data={dashboard} />
          ) : !selectedEqId ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-200" />
                <p className="text-sm font-medium text-slate-500">Selectati o liza din lista</p>
                <p className="text-xs text-slate-400 mt-1">pentru a vedea istoricul interventiilor</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              {/* Equipment Header */}
              <div className="px-4 py-3 border-b bg-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button onClick={() => setSelectedEqId(null)} className="lg:hidden p-1 -ml-1 rounded-lg hover:bg-slate-100" data-testid="mobile-back-btn">
                      <ChevronLeft className="w-5 h-5 text-slate-500" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-base font-bold text-slate-900">{selectedEq?.denumire}</h2>
                      <p className="text-[11px] text-slate-500">{selectedEq?.code} · {selectedEq?.marca} · {selectedEq?.serie_utilaj}</p>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    <div className="text-right mr-3">
                      <p className="text-[10px] text-slate-400">Index ore</p>
                      <p className="text-sm font-bold text-blue-700 flex items-center gap-1"><Gauge className="w-3.5 h-3.5" />{selectedEq?.ore_functionare || '—'} h</p>
                    </div>
                    <Button size="sm" className="h-8 text-xs gap-1" onClick={openNewForm} data-testid="new-intervention-btn">
                      <Plus className="w-3.5 h-3.5" /> Interventie noua
                    </Button>
                  </div>
                </div>
              </div>

              {/* Interventions List */}
              <div className="p-4 space-y-3">
                {eqInterventions.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-10 h-10 mx-auto mb-2 text-slate-200" />
                    <p className="text-sm text-slate-400">Nicio interventie inregistrata</p>
                    <Button size="sm" className="mt-3 text-xs gap-1" onClick={openNewForm}>
                      <Plus className="w-3.5 h-3.5" /> Prima interventie
                    </Button>
                  </div>
                ) : (
                  eqInterventions.map(iv => {
                    const pBadge = getPriorityBadge(iv.prioritate);
                    const sBadge = getStatusBadge(iv.status);
                    return (
                      <div key={iv.id} className="bg-white rounded-xl border shadow-sm p-4" data-testid={`intervention-${iv.id}`}>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-500 font-mono">{iv.nr_tichet}</span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${sBadge.color}`}>{sBadge.label}</span>
                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${pBadge.color}`}>{pBadge.label}</span>
                            <span className="text-[10px] text-slate-400 capitalize">{iv.tip?.replace('_', ' ')}</span>
                          </div>
                          <div className="flex gap-1">
                            {iv.status === 'deschis' && (
                              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => updateStatus(iv.id, 'in_lucru')}>In Lucru</Button>
                            )}
                            {iv.status === 'in_lucru' && (
                              <Button size="sm" variant="outline" className="h-6 text-[10px] px-2 text-emerald-600" onClick={() => updateStatus(iv.id, 'finalizat')}>Finalizeaza</Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEditForm(iv)}><Edit className="w-3 h-3" /></Button>
                            {hasRole('admin', 'manager') && (
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => deleteIntervention(iv.id)}><Trash2 className="w-3 h-3" /></Button>
                            )}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-xs mb-2">
                          <div><span className="text-slate-400">Data:</span> <span className="font-medium">{iv.data_interventie}</span></div>
                          <div><span className="text-slate-400">Index:</span> <span className="font-medium">{iv.index_ore ? `${iv.index_ore} h` : '—'}</span></div>
                          <div><span className="text-slate-400">Tehnician:</span> <span className="font-medium">{iv.technician_name || '—'}</span></div>
                          {iv.data_finalizare && <div><span className="text-slate-400">Finalizat:</span> <span className="font-medium">{iv.data_finalizare}</span></div>}
                        </div>
                        {iv.descriere && <p className="text-xs text-slate-600 mb-1"><span className="text-slate-400">Problema:</span> {iv.descriere}</p>}
                        {iv.lucrari_efectuate && <p className="text-xs text-slate-600 mb-1"><span className="text-slate-400">Lucrari:</span> {iv.lucrari_efectuate}</p>}
                        {iv.consumabile && iv.consumabile.length > 0 && (
                          <div className="mt-2 bg-slate-50 rounded-lg p-2">
                            <p className="text-[10px] text-slate-400 font-semibold mb-1 flex items-center gap-1"><Package className="w-3 h-3" /> Consumabile ({iv.consumabile.length})</p>
                            <div className="space-y-0.5">
                              {iv.consumabile.map((c, i) => (
                                <div key={i} className="text-[11px] flex justify-between">
                                  <span className="text-slate-600">{c.article_name} <span className="text-slate-400 font-mono">({c.article_code})</span></span>
                                  <span className="font-semibold">{c.quantity} {c.unit}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Intervention Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base">{editIntervention ? 'Editeaza Interventie' : 'Interventie Noua'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500">Tip Interventie</Label>
                <Select value={form.tip} onValueChange={v => setForm({...form, tip: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPS.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Prioritate</Label>
                <Select value={form.prioritate} onValueChange={v => setForm({...form, prioritate: v})}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITATI.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] text-slate-500">Data Interventie</Label>
                <Input className="h-8 text-xs" value={form.data_interventie} onChange={e => setForm({...form, data_interventie: e.target.value})} placeholder="zz.ll.aaaa" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Index Ore Functionare</Label>
                <Input className="h-8 text-xs" type="number" value={form.index_ore} onChange={e => setForm({...form, index_ore: e.target.value})} placeholder="ore" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Tehnician</Label>
                <Select value={form.technician_id || '__none__'} onValueChange={v => {
                  if (v === '__none__') { setForm({...form, technician_id: '', technician_name: ''}); return; }
                  const u = users.find(u => u.id === v);
                  setForm({...form, technician_id: v, technician_name: u?.full_name || ''});
                }}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Selectati..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">-- Selectati --</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Descriere Problema</Label>
              <Textarea className="text-xs h-16" value={form.descriere} onChange={e => setForm({...form, descriere: e.target.value})} />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Lucrari Efectuate</Label>
              <Textarea className="text-xs h-16" value={form.lucrari_efectuate} onChange={e => setForm({...form, lucrari_efectuate: e.target.value})} />
            </div>

            {/* Consumabile */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[10px] text-slate-500 flex items-center gap-1"><Package className="w-3 h-3" /> Consumabile Utilizate</Label>
                <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1" onClick={startConsumScan} data-testid="scan-consumabil-btn">
                  <Camera className="w-3 h-3" /> Scan QR
                </Button>
              </div>
              {showQrScanner && (
                <div className="relative mb-2 rounded-lg overflow-hidden border">
                  <div id="lize-qr-reader" style={{ width: '100%', minHeight: '200px' }} />
                  <button className="absolute top-2 right-2 bg-black/60 text-white p-1.5 rounded-full" onClick={async () => { await stopQr(); setShowQrScanner(false); }}>
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {form.consumabile.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs flex-1 truncate">{c.article_name} <span className="text-slate-400">({c.article_code})</span></span>
                  <Input className="h-7 w-20 text-xs text-center" type="number" value={c.quantity} onChange={e => updateConsumabilQty(idx, e.target.value)} />
                  <span className="text-[10px] text-slate-400 w-8">{c.unit}</span>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeConsumabil(idx)}><X className="w-3 h-3" /></Button>
                </div>
              ))}
              <div className="relative mt-2">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="h-7 text-xs pl-7" placeholder="Cauta articol..." value={articleSearch}
                  onChange={e => { setArticleSearch(e.target.value); fetchArticles(e.target.value); }} />
              </div>
              {articleSearch && articles.length > 0 && (
                <div className="border rounded mt-1 max-h-32 overflow-y-auto bg-white">
                  {articles.slice(0, 8).map(a => (
                    <button key={a.id} onClick={() => addConsumabil(a)}
                      className="w-full text-left px-2 py-1.5 text-xs hover:bg-slate-50 border-b last:border-0">
                      <span className="font-medium">{a.name}</span> <span className="text-slate-400">{a.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-[10px] text-slate-500">Observatii</Label>
              <Textarea className="text-xs h-12" value={form.observatii} onChange={e => setForm({...form, observatii: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Anuleaza</Button>
            <Button size="sm" onClick={saveIntervention} data-testid="save-intervention-btn">
              {editIntervention ? 'Salveaza' : 'Creaza Interventie'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Dashboard Sub-component
const DashboardView = ({ data }) => {
  if (!data) return <div className="flex-1 flex items-center justify-center text-slate-400">Se incarca...</div>;
  const { stats, top_equipment, top_consumables } = data;
  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="service-dashboard">
      <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-blue-500" /> Dashboard Service Lize
      </h2>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: 'Total Interventii', value: stats.total, color: 'text-slate-800', bg: 'bg-white' },
          { label: 'Deschise', value: stats.deschis, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'In Lucru', value: stats.in_lucru, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Finalizate', value: stats.finalizat, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'Urgente Active', value: stats.urgente, color: 'text-red-600', bg: 'bg-red-50' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-3 shadow-sm ${c.bg}`}>
            <p className="text-[9px] text-slate-500 uppercase font-semibold tracking-wider">{c.label}</p>
            <p className={`text-2xl font-bold mt-0.5 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Echipamente (cele mai multe interventii)</h3>
          <div className="space-y-2">
            {top_equipment.map((eq, i) => (
              <div key={eq.equipment_id} className="flex items-center gap-3">
                <span className="text-[10px] font-bold text-slate-400 w-4">{i + 1}</span>
                <span className="text-xs text-slate-700 flex-1 truncate">{eq.equipment_name}</span>
                <span className="text-xs font-mono text-slate-400">{eq.equipment_code}</span>
                <span className="text-xs font-bold text-blue-600">{eq.count}</span>
              </div>
            ))}
            {top_equipment.length === 0 && <p className="text-xs text-slate-400">Nicio interventie</p>}
          </div>
        </div>

        <div className="bg-white rounded-xl border p-4 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-3">Top Consumabile Utilizate</h3>
          <div className="space-y-2">
            {top_consumables.map((c, i) => (
              <div key={c.name} className="flex items-center gap-3">
                <span className="text-[10px] font-bold text-slate-400 w-4">{i + 1}</span>
                <span className="text-xs text-slate-700 flex-1 truncate">{c.name}</span>
                <span className="text-xs font-mono text-slate-400">{c.code}</span>
                <span className="text-xs font-bold text-emerald-600">{c.total_qty} {c.unit}</span>
              </div>
            ))}
            {top_consumables.length === 0 && <p className="text-xs text-slate-400">Niciun consumabil</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceLizePage;
